// Users Service - Service layer for user-related API operations

import axiosInstance, { msGraphInstance } from "../instance/axiosInstance";
import {
  USERS_ENDPOINTS,
  MS_GRAPH_ENDPOINTS,
  USER_MANAGEMENT_ENDPOINTS,
} from "../endpoints/users.endpoints";
import { serializeParams } from "../utils/paramSerializer";
import { handleApiError } from "../utils/errorHandler";
import { markNotificationRead } from "../../store/slices/notificationsSlice";

export const UsersService = {
  // Get current user profile
  getCurrentUser: async () => {
    try {
      const response = await axiosInstance.get(USERS_ENDPOINTS.GET_CURRENT);
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  // Update current user profile
  updateCurrentUser: async (updateData) => {
    try {
      const response = await axiosInstance.put(
        USERS_ENDPOINTS.UPDATE_CURRENT,
        updateData,
      );
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  // Get user by ID
  getUserById: async (userId) => {
    if (!userId) {
      throw new Error("User ID is required");
    }

    try {
      const response = await axiosInstance.get(
        USERS_ENDPOINTS.GET_BY_ID(userId),
      );
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  // Get user notifications
  // getNotifications: async (email) => {
  getNotifications: async (email, role, roleId) => {
    if (!email) {
      throw new Error("User email is required to fetch notifications");
    }
    if (!role) {
      throw new Error("User role is required to fetch notifications");
    }
    if (!roleId) {
      throw new Error("User roleId is required to fetch notifications");
    }
    try {
      const response = await axiosInstance.get(
        // USERS_ENDPOINTS.GET_NOTIFICATIONS(email),
        USERS_ENDPOINTS.GET_NOTIFICATIONS(email, role, roleId),
      );
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  // Mark notifications as read
  markNotificationsRead: async (notificationIds) => {
    try {
      notificationIds.array.forEach(async (id) => {
        const response = await axiosInstance.patch(
          USERS_ENDPOINTS.MARK_NOTIFICATIONS_READ,
          { notificationIds },
        );
        return response.data;
      });
    } catch (error) {
      throw handleApiError(error);
    }
  },

  // Mark notification action taken
  markNotificationActionTaken: async (notificationId, orderId) => {
    try {
      const response = await axiosInstance.put(
        USERS_ENDPOINTS.MARK_NOTIFICATION_ACTION_TAKEN,
        {
          // Send an empty string when notificationId is unavailable for order-level action taken.
          // notificationId: notificationId ? String(notificationId) : "",
          // orderId: String(orderId),
          // Normalize optional ids so the API never receives the literal string "undefined".
          notificationId:
            notificationId == null
              ? ""
              : notificationId
                ? String(notificationId)
                : "",
          orderId: orderId == null ? "" : String(orderId),
        },
      );
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  markNotificationRead: async (notificationId) => {
    if (!notificationId) {
      throw new Error("Notification ID is required");
    }
    try {
      const response = await axiosInstance.patch(
        USERS_ENDPOINTS.MARK_NOTIFICATIONS_READ(notificationId),
      );
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },
};

export const UserManageemntService = {
  // Fetch all users for user management
  fetchAllUsers: async () => {
    try {
      const response = await axiosInstance.get(
        USER_MANAGEMENT_ENDPOINTS.GET_ALL_USERS,
      );
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },
  addUser: async (userData) => {
    try {
      const response = await axiosInstance.post(
        USER_MANAGEMENT_ENDPOINTS.CREATE_USER,
        userData,
      );
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },
  fetchUserRoles: async (userId) => {
    if (!userId) {
      throw new Error("User ID is required");
    }
    try {
      const response = await axiosInstance.get(
        USER_MANAGEMENT_ENDPOINTS.GET_USER_ROLES(userId),
      );
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },
};

// Microsoft Graph Service - for Azure AD user operations
export const MSGraphService = {
  // Get current user from Microsoft Graph
  getCurrentUser: async () => {
    try {
      const response = await msGraphInstance.get(MS_GRAPH_ENDPOINTS.GET_ME);
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  // Get current user's photo
  getCurrentUserPhoto: async () => {
    try {
      const response = await msGraphInstance.get(
        MS_GRAPH_ENDPOINTS.GET_MY_PHOTO,
        {
          responseType: "arraybuffer",
        },
      );
      return response.data;
    } catch (error) {
      if (error.response?.status === 404) {
        return null;
      }
      throw handleApiError(error);
    }
  },

  // Get user photo by ID
  getUserPhoto: async (userId) => {
    if (!userId) {
      throw new Error("User ID is required");
    }

    try {
      const response = await msGraphInstance.get(
        MS_GRAPH_ENDPOINTS.GET_USER_PHOTO(userId),
        {
          responseType: "arraybuffer",
        },
      );
      return response.data;
    } catch (error) {
      if (error.response?.status === 404) {
        return null;
      }
      throw handleApiError(error);
    }
  },

  // Search users in Azure AD
  searchUsers: async (query, top = 10) => {
    if (!query) {
      return [];
    }

    try {
      const response = await msGraphInstance.get(
        MS_GRAPH_ENDPOINTS.SEARCH_USERS,
        {
          params: {
            $filter: `startswith(displayName,'${query}') or startswith(mail,'${query}')`,
            $top: top,
            $select:
              "id,displayName,mail,userPrincipalName,jobTitle,department",
          },
        },
      );
      return response.data?.value || [];
    } catch (error) {
      throw handleApiError(error);
    }
  },
};

export default {
  UsersService,
  MSGraphService,
};
